/**
 * V167.3 — 5-Day E-shop Growth Challenge landing page
 *
 * Cold-traffic LP modeled after the Pedro Adao / Russell Brunson challenge
 * funnel pattern (doubleacademy.cz reference). Captures email, fires:
 *   - magic-link for instant dashboard access
 *   - 5-day drip campaign (welcome + day 1-5 task emails + day 6 last-chance)
 *
 * Anti-pattern decisions vs reference:
 *   - NO yellow/black info-marketer aesthetic (we are B2B SaaS, not course)
 *   - NO founder-face-with-cap hero (product-led, not personality-led)
 *   - NO "master/mistr" course-creator naming
 *
 * Kept patterns (proven high CVR):
 *   - Real countdown to specific date
 *   - Single green CTA, no decision fatigue
 *   - 5-day commitment frame (vs 1-shot diagnostic)
 *   - "Reserve my spot" psychological scarcity
 *   - Multiple "100% FREE" assurances
 *   - Negative framing of competitors (no discounts, no agency, no churn)
 *
 * Routes: /vyzva-5-dni, /5-day-challenge, /challenge
 *
 * Supported languages: sk (default), en, de, pl, hu, cs
 */
import { useEffect, useState, useMemo, useRef } from "react";
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import { API } from "../config";
import { useLanguage } from "../contexts/LanguageContext";
import {
  Loader2, CheckCircle2, Clock, Lock, ArrowRight, Calendar, Mail, Eye, TrendingDown, ShieldAlert, Zap, Users,
} from "lucide-react";

const LANGS = ["sk", "en", "de", "pl", "hu", "cs"];

const COPY = {
  sk: {
    eyebrow: "5-dňová živá výzva · 100 % zdarma",
    h1_a: "Tvoj e-shop dostane",
    h1_b: "kompletný audit",
    h1_c: "za 5 dní.",
    h1_sub: "Bez slevy. Bez agentúry. Bez kupovania ďalšieho nástroja.",
    countdown_label: "Najbližší kohort štartuje za",
    days: "dní", hours: "hodín", minutes: "minút", seconds: "sekúnd",
    starts_at: "Štart {date} o 9:00",
    days_title: "Čo dostaneš každý deň",
    daily_tasks: [
      { Icon: Eye, day: "Deň 1", title: "Diagnostika", text: "Kompletný sken e-shopu — viditeľnosť, konverzia, prevádzka. TOP 3 bolestivé body s konkrétnymi opravami." },
      { Icon: TrendingDown, day: "Deň 2", title: "SEO Blueprint", text: "Plán pre tvojich top 10 produktov: chýbajúca schema, duplicitné titulky, AI-search visibility, 1 najvyššia ROI oprava na produkt." },
      { Icon: ShieldAlert, day: "Deň 3", title: "Conversion Audit", text: "Kde ti tečú peniaze — košík, checkout, trust signály, doprava. Heatmapa + 3 najrýchlejšie opravy." },
      { Icon: Zap, day: "Deň 4", title: "AI viditeľnosť", text: "Ako sa tvoj obchod ukazuje (alebo neukazuje) v ChatGPT, Perplexity, Gemini. 3 akcie na získanie citácií za 30 dní." },
      { Icon: Users, day: "Deň 5", title: "Live Q&A + Plán", text: "Konsolidovaný akčný plán + personalizovaná ponuka. Live otázky-odpovede so zakladateľom." },
    ],
    why_title: "Bez slevovania. Bez tlačenia. Bez boja o najnižšiu cenu.",
    why_lines: [
      "Bez Ahrefs za €199/mes, ktorý ti len reportuje problémy.",
      "Bez SEO agentúry za €2 000/mes a 6-mes onboardingu.",
      "Bez ďalšieho nástroja v tvojom dashboarde, ktorý raz za polrok otvoríš.",
      "5 dní, 5 úloh, 5 výsledkov. Potom sa rozhodneš sám.",
    ],
    form_title: "Rezervuj si miesto na ďalšiu výzvu",
    form_email_placeholder: "ty@firma.sk",
    form_button: "Rezervovať miesto zdarma",
    form_button_sending: "Posielam…",
    form_sub: "Bez kreditnej karty · 100 % zdarma · Odhlásiš sa jedným klikom",
    success_h1: "Si dnu. Skontroluj inbox.",
    success_body: "Poslali sme ti uvítací email s magic-linkom. Otvor ho — tam nájdeš všetko potrebné pred Deň 1.",
    success_close: "Zatvoriť",
    enrolled_strip: "{n} účastníkov sa už zaregistrovalo na tento kohort",
    proof_title: "100 % zdarma. Žiadny haček.",
    proof_lines: [
      "Nepýtame kartu.",
      "Nezapisujeme ťa do žiadneho \"premium plánu\".",
      "Na konci Dňa 5 dostaneš ponuku — povieš áno alebo nie.",
      "Aj keď povieš nie, audit a plán zostávajú tebe.",
    ],
  },
  en: {
    eyebrow: "5-day live challenge · 100 % free",
    h1_a: "Your e-shop gets a",
    h1_b: "complete audit",
    h1_c: "in 5 days.",
    h1_sub: "No discount. No agency. No new tool subscription.",
    countdown_label: "Next cohort starts in",
    days: "days", hours: "hours", minutes: "minutes", seconds: "seconds",
    starts_at: "Starts {date} at 9 AM",
    days_title: "What you get each day",
    daily_tasks: [
      { Icon: Eye, day: "Day 1", title: "Diagnostic", text: "Full e-shop scan — visibility, conversion, ops. Top 3 pain points with specific fixes." },
      { Icon: TrendingDown, day: "Day 2", title: "SEO Blueprint", text: "Plan for your top 10 products: missing schema, duplicate titles, AI-search visibility, 1 highest-ROI fix per product." },
      { Icon: ShieldAlert, day: "Day 3", title: "Conversion Audit", text: "Where your money leaks — cart, checkout, trust, shipping. Heatmap + 3 fastest fixes." },
      { Icon: Zap, day: "Day 4", title: "AI Visibility", text: "How your store appears (or doesn't) in ChatGPT, Perplexity, Gemini. 3 actions to get cited in 30 days." },
      { Icon: Users, day: "Day 5", title: "Live Q&A + Plan", text: "Consolidated action plan + personalized offer. Live Q&A with the founder." },
    ],
    why_title: "No discount games. No pressure. No race to the bottom.",
    why_lines: [
      "No Ahrefs at €199/mo that only reports problems.",
      "No SEO agency at €2,000/mo with 6-month onboarding.",
      "No another tool in your dashboard you open twice a year.",
      "5 days, 5 tasks, 5 outcomes. You decide what's next.",
    ],
    form_title: "Reserve your spot for the next challenge",
    form_email_placeholder: "you@company.com",
    form_button: "Reserve my spot for free",
    form_button_sending: "Sending…",
    form_sub: "No credit card · 100 % free · Unsubscribe with one click",
    success_h1: "You're in. Check your inbox.",
    success_body: "We sent a welcome email with a magic link. Open it — everything you need before Day 1 is there.",
    success_close: "Close",
    enrolled_strip: "{n} participants already signed up for this cohort",
    proof_title: "100 % free. No catch.",
    proof_lines: [
      "We don't ask for your card.",
      "You're not auto-enrolled in any \"premium plan\".",
      "On Day 5 you get an offer — yes or no.",
      "Even if you say no, the audit and plan stay yours.",
    ],
  },
  de: {
    eyebrow: "5-tägige Live-Challenge · 100 % kostenlos",
    h1_a: "Dein E-Shop bekommt ein",
    h1_b: "vollständiges Audit",
    h1_c: "in 5 Tagen.",
    h1_sub: "Kein Rabatt. Keine Agentur. Kein neues Tool-Abo.",
    countdown_label: "Nächste Kohorte startet in",
    days: "Tage", hours: "Std", minutes: "Min", seconds: "Sek",
    starts_at: "Start {date} um 9 Uhr",
    days_title: "Was du täglich bekommst",
    daily_tasks: [
      { Icon: Eye, day: "Tag 1", title: "Diagnose", text: "Vollständiger Shop-Scan — Sichtbarkeit, Conversion, Operations. Top 3 Probleme mit konkreten Fixes." },
      { Icon: TrendingDown, day: "Tag 2", title: "SEO Blueprint", text: "Plan für deine Top-10-Produkte: fehlende Schemas, doppelte Titel, AI-Sichtbarkeit, 1 höchster ROI-Fix pro Produkt." },
      { Icon: ShieldAlert, day: "Tag 3", title: "Conversion Audit", text: "Wo dein Geld leckt — Warenkorb, Checkout, Trust, Versand. Heatmap + 3 schnellste Fixes." },
      { Icon: Zap, day: "Tag 4", title: "AI-Sichtbarkeit", text: "Wie dein Shop in ChatGPT, Perplexity, Gemini erscheint. 3 Aktionen für Zitate in 30 Tagen." },
      { Icon: Users, day: "Tag 5", title: "Live Q&A + Plan", text: "Konsolidierter Aktionsplan + personalisiertes Angebot. Live Q&A mit dem Gründer." },
    ],
    why_title: "Keine Rabattspiele. Kein Druck. Kein Wettlauf nach unten.",
    why_lines: [
      "Kein Ahrefs für 199 €/Mt., das nur Probleme meldet.",
      "Keine SEO-Agentur für 2.000 €/Mt. mit 6-Monats-Onboarding.",
      "Kein weiteres Dashboard-Tool, das du 2× im Jahr öffnest.",
      "5 Tage, 5 Aufgaben, 5 Ergebnisse. Du entscheidest, was als Nächstes kommt.",
    ],
    form_title: "Reserviere deinen Platz für die nächste Challenge",
    form_email_placeholder: "du@firma.de",
    form_button: "Platz kostenlos sichern",
    form_button_sending: "Sende…",
    form_sub: "Keine Karte nötig · 100 % kostenlos · Mit einem Klick abmelden",
    success_h1: "Du bist dabei. Schau in dein Postfach.",
    success_body: "Wir haben dir eine Willkommens-E-Mail mit Magic-Link geschickt. Öffne sie — alles für Tag 1 ist drin.",
    success_close: "Schließen",
    enrolled_strip: "{n} Teilnehmer bereits für diese Kohorte angemeldet",
    proof_title: "100 % kostenlos. Kein Haken.",
    proof_lines: [
      "Wir fragen nicht nach deiner Karte.",
      "Du wirst nicht automatisch in einen \"Premium-Plan\" eingeschrieben.",
      "An Tag 5 bekommst du ein Angebot — Ja oder Nein.",
      "Auch wenn du Nein sagst, Audit und Plan bleiben dir.",
    ],
  },
  pl: {
    eyebrow: "5-dniowe wyzwanie na żywo · 100 % za darmo",
    h1_a: "Twój e-sklep dostanie",
    h1_b: "pełny audyt",
    h1_c: "w 5 dni.",
    h1_sub: "Bez rabatu. Bez agencji. Bez subskrypcji kolejnego narzędzia.",
    countdown_label: "Następna grupa startuje za",
    days: "dni", hours: "godz", minutes: "min", seconds: "sek",
    starts_at: "Start {date} o 9:00",
    days_title: "Co dostajesz każdego dnia",
    daily_tasks: [
      { Icon: Eye, day: "Dzień 1", title: "Diagnostyka", text: "Pełny skan sklepu — widoczność, konwersja, operacje. TOP 3 problemy z konkretnymi rozwiązaniami." },
      { Icon: TrendingDown, day: "Dzień 2", title: "SEO Blueprint", text: "Plan dla twoich top 10 produktów: brakujące schema, duplikaty tytułów, AI-search, 1 najwyższy ROI fix na produkt." },
      { Icon: ShieldAlert, day: "Dzień 3", title: "Conversion Audit", text: "Gdzie tracisz pieniądze — koszyk, checkout, zaufanie, dostawa. Heatmapa + 3 najszybsze poprawki." },
      { Icon: Zap, day: "Dzień 4", title: "Widoczność AI", text: "Jak twój sklep pojawia się (lub nie) w ChatGPT, Perplexity, Gemini. 3 akcje, żeby być cytowanym w 30 dni." },
      { Icon: Users, day: "Dzień 5", title: "Live Q&A + Plan", text: "Skonsolidowany plan działania + personalizowana oferta. Live Q&A z założycielem." },
    ],
    why_title: "Bez gier rabatowych. Bez presji. Bez wyścigu na dno.",
    why_lines: [
      "Bez Ahrefs za 199 €/mies, które tylko raportuje problemy.",
      "Bez agencji SEO za 2 000 €/mies i 6-miesięcznego onboardingu.",
      "Bez kolejnego narzędzia w dashboardzie, które otwierasz raz w roku.",
      "5 dni, 5 zadań, 5 wyników. Sam decydujesz, co dalej.",
    ],
    form_title: "Zarezerwuj miejsce na następne wyzwanie",
    form_email_placeholder: "ty@firma.pl",
    form_button: "Zarezerwuj miejsce za darmo",
    form_button_sending: "Wysyłam…",
    form_sub: "Bez karty · 100 % za darmo · Wypisz się jednym kliknięciem",
    success_h1: "Jesteś w środku. Sprawdź skrzynkę.",
    success_body: "Wysłaliśmy ci powitalny email z magic linkiem. Otwórz go — wszystko, czego potrzebujesz przed Dniem 1, jest tam.",
    success_close: "Zamknij",
    enrolled_strip: "{n} uczestników już zapisanych na tę grupę",
    proof_title: "100 % za darmo. Bez haczyka.",
    proof_lines: [
      "Nie prosimy o kartę.",
      "Nie zapisujemy cię automatycznie do żadnego \"planu premium\".",
      "W Dniu 5 dostajesz ofertę — tak lub nie.",
      "Nawet jeśli powiesz nie, audyt i plan zostają z tobą.",
    ],
  },
  hu: {
    eyebrow: "5 napos élő kihívás · 100 % ingyenes",
    h1_a: "A te webshopod kap egy",
    h1_b: "teljes auditot",
    h1_c: "5 nap alatt.",
    h1_sub: "Kedvezmény nélkül. Ügynökség nélkül. Újabb eszköz előfizetése nélkül.",
    countdown_label: "A következő kohorsz indul",
    days: "nap", hours: "óra", minutes: "perc", seconds: "mp",
    starts_at: "Indulás {date} 9:00-kor",
    days_title: "Mit kapsz minden nap",
    daily_tasks: [
      { Icon: Eye, day: "1. nap", title: "Diagnózis", text: "Teljes shop scan — láthatóság, konverzió, működés. TOP 3 fájó pont konkrét javításokkal." },
      { Icon: TrendingDown, day: "2. nap", title: "SEO Blueprint", text: "Terv a top 10 termékedre: hiányzó schema, duplikált címek, AI-search, 1 legmagasabb ROI fix terméken." },
      { Icon: ShieldAlert, day: "3. nap", title: "Conversion Audit", text: "Hol szivárog a pénz — kosár, checkout, bizalom, szállítás. Hőtérkép + 3 leggyorsabb javítás." },
      { Icon: Zap, day: "4. nap", title: "AI láthatóság", text: "Hogyan jelenik meg (vagy nem) a shopod ChatGPT-ben, Perplexity-ben, Geminiben. 3 lépés 30 napon belül." },
      { Icon: Users, day: "5. nap", title: "Élő Q&A + Terv", text: "Konszolidált akcióterv + személyre szabott ajánlat. Élő Q&A az alapítóval." },
    ],
    why_title: "Kedvezmény-játékok nélkül. Nyomás nélkül. Verseny nélkül a legalsóért.",
    why_lines: [
      "Ahrefs nélkül 199 €/hó, ami csak jelenti a problémákat.",
      "SEO ügynökség nélkül 2 000 €/hó és 6 hónapos onboardinggal.",
      "Még egy eszköz nélkül a dashboardodban, amit évente kétszer nyitsz meg.",
      "5 nap, 5 feladat, 5 eredmény. Te döntöd el, mi következik.",
    ],
    form_title: "Foglald le a helyed a következő kihívásra",
    form_email_placeholder: "te@ceg.hu",
    form_button: "Helyem ingyenes lefoglalása",
    form_button_sending: "Küldés…",
    form_sub: "Nincs kártya · 100 % ingyenes · Egy kattintással leiratkozhatsz",
    success_h1: "Bent vagy. Nézd meg a beérkezett leveleket.",
    success_body: "Küldtünk egy üdvözlő emailt magic linkkel. Nyisd meg — minden ott van az 1. nap előtt.",
    success_close: "Bezár",
    enrolled_strip: "{n} résztvevő már feliratkozott erre a kohorszra",
    proof_title: "100 % ingyenes. Nincs csapda.",
    proof_lines: [
      "Nem kérünk kártyát.",
      "Nem automatikusan iratkozol fel \"premium csomagra\".",
      "Az 5. napon kapsz egy ajánlatot — igen vagy nem.",
      "Ha nemet mondasz, az audit és a terv akkor is a tiéd marad.",
    ],
  },
  cs: {
    eyebrow: "5denní živá výzva · 100 % zdarma",
    h1_a: "Tvůj e-shop dostane",
    h1_b: "kompletní audit",
    h1_c: "za 5 dní.",
    h1_sub: "Bez slev. Bez agentury. Bez kupování dalšího nástroje.",
    countdown_label: "Další kohorta startuje za",
    days: "dní", hours: "hodin", minutes: "minut", seconds: "sekund",
    starts_at: "Start {date} v 9:00",
    days_title: "Co dostaneš každý den",
    daily_tasks: [
      { Icon: Eye, day: "Den 1", title: "Diagnostika", text: "Kompletní sken e-shopu — viditelnost, konverze, provoz. TOP 3 bolavé body s konkrétními opravami." },
      { Icon: TrendingDown, day: "Den 2", title: "SEO Blueprint", text: "Plán pro tvých top 10 produktů: chybějící schema, duplicitní titulky, AI-search, 1 nejvyšší ROI oprava na produkt." },
      { Icon: ShieldAlert, day: "Den 3", title: "Conversion Audit", text: "Kde ti tečou peníze — košík, checkout, trust signály, doprava. Heatmapa + 3 nejrychlejší opravy." },
      { Icon: Zap, day: "Den 4", title: "AI viditelnost", text: "Jak se tvůj obchod ukazuje (nebo neukazuje) v ChatGPT, Perplexity, Gemini. 3 akce pro citace za 30 dní." },
      { Icon: Users, day: "Den 5", title: "Live Q&A + Plán", text: "Konsolidovaný akční plán + personalizovaná nabídka. Live Q&A se zakladatelem." },
    ],
    why_title: "Bez slevování. Bez tlačení. Bez boje o nejnižší cenu.",
    why_lines: [
      "Bez Ahrefs za 199 €/měs, který ti jen reportuje problémy.",
      "Bez SEO agentury za 2 000 €/měs a 6-měs onboardingu.",
      "Bez dalšího nástroje v tvém dashboardu, který otevřeš dvakrát ročně.",
      "5 dní, 5 úloh, 5 výsledků. Sám se rozhodneš, co dál.",
    ],
    form_title: "Rezervuj si místo na další výzvu",
    form_email_placeholder: "ty@firma.cz",
    form_button: "Rezervovat místo zdarma",
    form_button_sending: "Posílám…",
    form_sub: "Bez karty · 100 % zdarma · Odhlásíš se jedním klikem",
    success_h1: "Jsi uvnitř. Zkontroluj inbox.",
    success_body: "Poslali jsme ti uvítací email s magic linkem. Otevři ho — všechno potřebné před Dnem 1 je tam.",
    success_close: "Zavřít",
    enrolled_strip: "{n} účastníků se už zaregistrovalo na tuto kohortu",
    proof_title: "100 % zdarma. Bez háčku.",
    proof_lines: [
      "Neptáme se na kartu.",
      "Nezapisujeme tě do žádného \"premium plánu\".",
      "Na konci Dne 5 dostaneš nabídku — řekneš ano nebo ne.",
      "I když řekneš ne, audit a plán zůstávají tobě.",
    ],
  },
};

function formatDate(iso, lang) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(lang || "sk", { day: "numeric", month: "long" });
  } catch { return ""; }
}

function CountdownBox({ value, label }) {
  return (
    <div className="bg-black/40 border border-emerald-500/30 rounded-lg p-3 sm:p-4 min-w-[68px] sm:min-w-[88px]">
      <div className="text-3xl sm:text-5xl font-black text-emerald-400 leading-none tabular-nums">{String(value).padStart(2, "0")}</div>
      <div className="text-[10px] sm:text-xs text-white/50 font-mono uppercase tracking-wider mt-1.5">{label}</div>
    </div>
  );
}

export default function ChallengePage() {
  const { language } = useLanguage();
  const lang = LANGS.includes(language) ? language : "sk";
  const c = COPY[lang];
  const [sp] = useSearchParams();
  const source = sp.get("src") || "direct";

  const [info, setInfo] = useState(null);
  const [now, setNow] = useState(() => new Date());
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const formRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/challenge/info`).then((r) => setInfo(r.data)).catch(() => {});
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const countdown = useMemo(() => {
    if (!info?.next_cohort_start_iso) return null;
    const target = new Date(info.next_cohort_start_iso);
    const diff = Math.max(0, target - now);
    const days = Math.floor(diff / 86400000);
    const hours = Math.floor((diff % 86400000) / 3600000);
    const minutes = Math.floor((diff % 3600000) / 60000);
    const seconds = Math.floor((diff % 60000) / 1000);
    return { days, hours, minutes, seconds, dateStr: formatDate(info.next_cohort_start_iso, lang) };
  }, [info, now, lang]);

  const submit = async (e) => {
    e?.preventDefault();
    setError("");
    if (!email.trim() || !/.+@.+\..+/.test(email)) { setError("Email"); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/challenge/enrol`, {
        email: email.trim(), language: lang, source,
      });
      setDone(true);
    } catch {
      setError("Skús znova.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white selection:bg-emerald-500/40">
      <header className="border-b border-white/5 px-6 py-4">
        <a href="/" className="inline-flex items-center gap-2 text-white font-black text-lg tracking-tight">
          <span className="text-emerald-400">▲</span> TopRankPulse
        </a>
      </header>

      {/* HERO */}
      <section className="px-6 pt-12 sm:pt-16 pb-12 max-w-4xl mx-auto" data-testid="challenge-hero">
        <div className="inline-flex items-center gap-2 px-3 py-1 mb-5 bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-[10px] font-mono uppercase tracking-widest font-bold rounded-full">
          <Calendar className="w-3 h-3"/> {c.eyebrow}
        </div>
        <h1 className="text-4xl sm:text-6xl lg:text-7xl font-black tracking-tight leading-[1.05] mb-5">
          <span className="text-white/85">{c.h1_a}</span>{" "}
          <span className="text-emerald-400">{c.h1_b}</span>{" "}
          <span className="text-white/85">{c.h1_c}</span>
        </h1>
        <p className="text-white/60 text-base sm:text-xl max-w-2xl mb-10">
          {c.h1_sub}
        </p>

        {/* Countdown */}
        {countdown && (
          <div className="bg-gradient-to-br from-emerald-500/10 to-transparent border border-emerald-500/20 rounded-2xl p-5 sm:p-6 mb-10" data-testid="challenge-countdown">
            <div className="text-xs sm:text-sm text-white/60 font-mono uppercase tracking-widest mb-3 flex items-center gap-2">
              <Clock className="w-4 h-4"/> {c.countdown_label}
            </div>
            <div className="flex flex-wrap gap-2 sm:gap-3">
              <CountdownBox value={countdown.days} label={c.days}/>
              <CountdownBox value={countdown.hours} label={c.hours}/>
              <CountdownBox value={countdown.minutes} label={c.minutes}/>
              <CountdownBox value={countdown.seconds} label={c.seconds}/>
            </div>
            <div className="text-xs text-white/50 mt-3 font-mono">
              {c.starts_at.replace("{date}", countdown.dateStr)}
            </div>
          </div>
        )}

        {/* Primary CTA */}
        <button
          onClick={() => formRef.current?.scrollIntoView({ behavior: "smooth", block: "center" })}
          data-testid="challenge-scroll-to-form"
          className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-black font-black text-base sm:text-lg transition-colors"
        >
          {c.form_button} <ArrowRight className="w-5 h-5"/>
        </button>
        {info && info.current_cohort_enrolled > 0 && (
          <div className="mt-3 text-xs text-white/40 font-mono inline-flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"/>
            {c.enrolled_strip.replace("{n}", info.current_cohort_enrolled)}
          </div>
        )}
      </section>

      {/* DAILY TASKS */}
      <section className="border-t border-white/5 px-6 py-16 max-w-4xl mx-auto" data-testid="challenge-tasks">
        <h2 className="text-2xl sm:text-3xl font-black tracking-tight mb-8 text-white/90">{c.days_title}</h2>
        <div className="space-y-3">
          {c.daily_tasks.map((t, i) => (
            <div key={i} className="bg-white/[0.02] border border-white/10 rounded-2xl p-5 sm:p-6 flex items-start gap-4">
              <div className="w-12 h-12 shrink-0 rounded-xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center">
                <t.Icon className="w-5 h-5 text-emerald-400"/>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] text-emerald-400 font-mono uppercase tracking-widest mb-0.5">{t.day}</div>
                <h3 className="text-lg sm:text-xl font-black text-white mb-1">{t.title}</h3>
                <p className="text-sm sm:text-base text-white/65">{t.text}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* WHY (anti-competitor framing) */}
      <section className="border-t border-white/5 px-6 py-16 max-w-3xl mx-auto" data-testid="challenge-why">
        <h2 className="text-3xl sm:text-4xl font-black tracking-tight mb-7 text-white">
          {c.why_title}
        </h2>
        <div className="space-y-3 text-lg sm:text-xl font-medium text-white/75 leading-snug">
          {c.why_lines.map((line, i) => (
            <p key={i} className={i === c.why_lines.length - 1 ? "text-emerald-300 font-black pt-2" : ""}>
              {line}
            </p>
          ))}
        </div>
      </section>

      {/* FORM */}
      <section ref={formRef} className="border-t border-white/5 px-6 py-16 max-w-2xl mx-auto" data-testid="challenge-form-section">
        <div className="bg-gradient-to-br from-emerald-500/10 to-transparent border border-emerald-500/30 rounded-2xl p-6 sm:p-8">
          {!done ? (
            <>
              <h2 className="text-2xl sm:text-3xl font-black tracking-tight mb-2">{c.form_title}</h2>
              {countdown && (
                <p className="text-sm text-white/50 font-mono mb-5">
                  {c.starts_at.replace("{date}", countdown.dateStr)}
                </p>
              )}
              <form onSubmit={submit} className="space-y-3">
                <div className="relative">
                  <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40"/>
                  <input
                    data-testid="challenge-email-input"
                    type="email"
                    placeholder={c.form_email_placeholder}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full pl-9 pr-4 py-3.5 bg-black/40 border border-white/15 rounded-lg text-white placeholder-white/30 focus:border-emerald-400 focus:outline-none"
                  />
                </div>
                {error && <div className="text-red-400 text-xs" data-testid="challenge-error">{error}</div>}
                <button
                  data-testid="challenge-submit"
                  type="submit"
                  disabled={submitting}
                  className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-black font-black text-base sm:text-lg transition-colors disabled:opacity-60"
                >
                  {submitting ? (
                    <><Loader2 className="w-5 h-5 animate-spin"/> {c.form_button_sending}</>
                  ) : (
                    <>{c.form_button} <ArrowRight className="w-5 h-5"/></>
                  )}
                </button>
              </form>
              <p className="text-[11px] text-white/40 mt-4 text-center inline-flex items-center justify-center gap-1.5 w-full">
                <Lock className="w-3 h-3"/> {c.form_sub}
              </p>
            </>
          ) : (
            <div className="text-center py-2" data-testid="challenge-success">
              <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
                <CheckCircle2 className="w-7 h-7 text-emerald-400"/>
              </div>
              <h2 className="text-2xl font-black text-white mb-3">{c.success_h1}</h2>
              <p className="text-white/60 text-sm sm:text-base mb-5">{c.success_body}</p>
              <button
                onClick={() => setDone(false)}
                className="px-5 py-2.5 rounded-lg bg-white/10 hover:bg-white/20 text-white text-sm font-bold transition-colors"
              >
                {c.success_close}
              </button>
            </div>
          )}
        </div>
      </section>

      {/* PROOF — no catch */}
      <section className="border-t border-white/5 px-6 py-16 max-w-3xl mx-auto" data-testid="challenge-proof">
        <h2 className="text-2xl sm:text-3xl font-black tracking-tight mb-5 text-emerald-300">
          {c.proof_title}
        </h2>
        <ul className="space-y-2 text-base sm:text-lg text-white/70">
          {c.proof_lines.map((line, i) => (
            <li key={i} className="flex items-start gap-2.5">
              <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5"/>
              <span>{line}</span>
            </li>
          ))}
        </ul>
      </section>

      <footer className="border-t border-white/5 px-6 py-8 text-center text-white/30 text-xs font-mono">
        © TopRankPulse · 5-Day Challenge · v1
      </footer>
    </div>
  );
}
